import { AxiosInstance } from "axios"
import { AppError } from "../../core/api/errorTypes"
import {
    AccountChangeType,
    ActivateIdCardBody,
    Authentication,
    AuthenticationResponse,
    CardSubscriptionCompaniesResponse,
    CompanyChoice,
    CompanyIdChoice,
    IdCardSubscription,
    UserType,
    ValidatePasswordRequest,
    ValidatePasswordResponse,
} from "./loginTypes"

export enum LoginQuery {
    ID_CARDS_SUBSCRIPTIONS = "id-cards-subscriptions",
    ID_CARD_SUBSCRIPTION_COMPANIES = "id-card-subscription-companies",
}

type SsoTenantDiscoveryParams = Readonly<{}>

type SsoTenantDiscoveryResponse = Readonly<{
    isSso: boolean
    tenantId?: string
    loginUrl?: string
}>

type AuthenticateByUsernameParams = Readonly<{
    username: string
}>

type ResetPasswordParams = Readonly<{
    email: string
}>

type AuthenticateByActivationCodeParams = Readonly<{
    type: string // Create enum for types
    value: string
}>

type CreatePasswordParams = Readonly<{
    oneTimeToken: string
    password: string
}>

type SelectCompanyParams = Readonly<{
    company: CompanyIdChoice
}>

type AuthenticateByPasswordParams = Readonly<{
    username: string
    password: string
    authentication: Authentication
}>

type ActivateAccountParams = Readonly<{
    username: string
    password: string
    authentication: Authentication
}>

export async function ssoTenantDiscovery(
    {}: SsoTenantDiscoveryParams,
    axios: AxiosInstance
): Promise<SsoTenantDiscoveryResponse> {
    const url = "/discovery"
    const response = await axios.get(url)
    return response.data
}

export async function authenticateByUsername(
    { username }: AuthenticateByUsernameParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/admin/username", {
        username,
    })

    return response.data
}

export async function resetPassword(
    { email }: ResetPasswordParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/resetPassword", {
        email,
    })

    return response.data
}

export async function authenticateByActivationCode(
    { type, value }: AuthenticateByActivationCodeParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/byType", {
        type,
        value,
    })

    return response.data
}

export async function createPassword(
    { oneTimeToken, password }: CreatePasswordParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/createPassword", {
        oneTimeToken,
        password,
    })

    return response.data
}

export async function selectCompany(
    { company }: SelectCompanyParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("authenticate/selectCompany", {
        company,
    })

    return response.data
}

export async function authenticateByPassword(
    { username, password, authentication }: AuthenticateByPasswordParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate", {
        username,
        password,
        authentication,
    })

    return response.data
}

type GetValidatePwRequest = Readonly<{
    userType: UserType
    companyId: number
    serviceCenterId: number | null
    userGroupId: number | null
    password: string
}>

export function getValidatePwRequest({
    userType,
    companyId,
    serviceCenterId,
    userGroupId,
    password,
}: GetValidatePwRequest): ValidatePasswordRequest {
    switch (userType) {
        case UserType.SUPPLIER:
            return {
                userType: UserType.SUPPLIER,
                supplierDetails: {
                    companyId,
                },
                password,
            }
        case UserType.MERCHANT:
            return {
                userType: UserType.MERCHANT,
                merchantDetails: {
                    companyId,
                },
                password,
            }
        case UserType.SERVICE_CENTER:
            if (!serviceCenterId)
                throw new AppError(
                    "E_INTERNAL_ERROR",
                    "Internal Error",
                    `A value for serviceCenterId must be provided for userType ${userType}`,
                )

            return {
                userType: UserType.SERVICE_CENTER,
                serviceCenterDetails: {
                    companyId,
                    serviceCenterId,
                },
                password,
            }
        case UserType.COMPANY:
            return {
                userType: UserType.COMPANY,
                companyDetails: {
                    companyId,
                },
                password,
            }
        case UserType.OFFICE_MANAGER:
            if (!userGroupId)
                throw new AppError(
                    "E_INTERNAL_ERROR",
                    "Internal Error",
                    `A value for userGroupId must be provided for userType ${userType}`,
                )

            return {
                userType: UserType.OFFICE_MANAGER,
                officeManagerDetails: {
                    companyId,
                    userGroupId,
                },
                password,
            }
        case UserType.USER:
            if (!userGroupId)
                throw new AppError(
                    "E_INTERNAL_ERROR",
                    "Internal Error",
                    `A value for userGroupId must be provided for userType ${userType}`,
                )
            return {
                userType: UserType.USER,
                companyUserDetails: {
                    companyId,
                    userGroupId: userGroupId,
                },
                password,
            }
        default:
            throw new AppError("E_INVALID_LINK", "Invalid invitation link", `Unsupported userType value: ${userType}`)
    }
}

export async function validatePassword(
    request: ValidatePasswordRequest,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ValidatePasswordResponse> {
    const url = "/authenticate/validatePassword"

    const response = await axios.post(url, request, {
        signal,
    })

    return response.data
}

export async function activateAccount(
    request: ActivateAccountParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/activateAdminAccount", request)
    return response.data
}

type ConfirmAccountChangeParams = Readonly<{
    changeType: AccountChangeType
    authentication: Authentication
}>

type ConfirmAccountChangeResponse = Readonly<{
    serviceResponse: {
        displayMessage: string
    }
}>

export async function confirmAccountChange(
    request: ConfirmAccountChangeParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ConfirmAccountChangeResponse> {
    const url = "/authenticate/confirmAccountChange"
    const response = await axios.post(url, request, { signal })
    return response.data
}

// ID card signup

export async function authenticateByActivateIdCard(
    request: ActivateIdCardBody,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AuthenticationResponse> {
    const { data } = await axios.post("/authenticate/activateIdCard", request, { signal })

    return data
}

type IdCardSubscriptionsParam = Readonly<{
    companyId: number
    userGroupId: number
    activationToken: string
}>

export type IdCardSubscriptionsResponse = Readonly<{
    cardSubscriptions: IdCardSubscription[]
}>

export async function fetchIdCardSubscriptions(
    params: IdCardSubscriptionsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<IdCardSubscriptionsResponse> {
    const { data } = await axios.get("/public/cards/register/cardSubscriptions", {
        params,
        signal,
    })

    return data
}

type CardSubscriptionCompaniesParam = Readonly<{
    activationToken: string
}>

export async function fetchCardSubscriptionCompanies(
    { activationToken }: CardSubscriptionCompaniesParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CardSubscriptionCompaniesResponse> {
    const { data } = await axios.get("/public/cards/register/companies", {
        params: {
            activationToken,
        },
        signal,
    })

    return data
}

type UserCreatePasswordParams = Readonly<{
    oneTimeToken: string
    password: string
}>

export async function activateUserAccount(
    request: UserCreatePasswordParams,
    axios: AxiosInstance,
): Promise<AuthenticationResponse> {
    const response = await axios.post("/authenticate/createPassword", request)
    return response.data
}
